import { z } from "zod";

import { messagePathPartSchema } from "@/shared/domain/topics";

import { Requirement } from "../LayoutRecord";

import { createLayoutDefinitionSchema } from "./createLayoutDefinitionSchema";

/**
 * Runtime validation for a StructuredRequirement held in persisted state.
 *
 * `topicMessagePath` and `dataType` are optional because they restate what
 * `topicName` and `messagePath` already say; a requirement arriving without
 * them still says what it matches.
 */
const StructuredRequirementSchema = z.object({
  topicName: z.string(),
  messagePath: z.array(messagePathPartSchema),
  topicMessagePath: z.string().optional(),
  dataType: z.string().optional(),
});

/**
 * Runtime validation for a JoinedPathRequirement held in persisted state,
 * written by clients that do not record the topic name and the message path
 * separately.
 */
const JoinedPathRequirementSchema = z.object({
  topicMessagePath: z.string(),
  // Any string rather than the CanonicalDataType enum, so a layout naming a
  // data type this application does not know still loads.
  dataType: z.string().optional(),
  // Readers pick a requirement's shape off `topicName` (see
  // isStructuredRequirement), so one that names a topic has to validate as the
  // split shape. Without this, a malformed `messagePath` would pass on the
  // joined fields saved beside it and load with parts nothing checked.
  topicName: z.never().optional(),
});

const LayoutRequirementSchema: z.ZodType<Requirement> = z.union([
  StructuredRequirementSchema,
  JoinedPathRequirementSchema,
]);

export const LayoutDefinitionSchema = createLayoutDefinitionSchema(
  LayoutRequirementSchema,
);
